<template>
  <div class="container-fluid" v-if="post">
    <div class="row">
      <div class="col-12">
        <h1 class="mb-0 mt-4">{{ $t($route.meta.title) }}</h1>
        <hr class="mt-0">
      </div>
    </div>
    <div class="row">
      <!-- Main area -->
      <div class="col-9">
        <div class="row">
          <div class="col-12">
            <ci-card type="dark">
              <label class="mt-3" :for="`${_uid}-title`">Titel</label>
              <input
                type="text"
                :class="getInputClass(`post_content.${step}.title`)"
                :id="`${_uid}-title`"
                placeholder="Titel eingeben…"
                @change="setSlug(post.getStep(step).title)"
                v-model="post.getStep(step).title"
                ref="title"
              >

              <label class="mt-3" :for="`${_uid}-slug`">Untertitel</label>
              <input
                type="text"
                class="form-control bg-light"
                placeholder="Untertitel eingeben…"
                :id="`${_uid}-subtitle`"
                v-model="post.getStep(step).subtitle"
                ref="subtitle"
              >

              <label class="mt-3" :for="`${_uid}-teaser`">Teasertext</label>
              <textarea
                class="form-control bg-light"
                :id="`${_uid}-teaser`"
                rows="3"
                v-model="post.getStep(step).teaser"
                placeholder="Teasertext eingeben…"
                ref="teaser"
              ></textarea>

              <div class="mt-4">
                <label>Beitrag</label>
                <ci-editor
                  :class="getEditorClass"
                  v-model="post.getStep(step).content"
                  @editor:ready="onEditorReadyStateChange"
                />
              </div>

              <div class="d-flex mb-4 mt-4">
                <div>
                  <button
                    class="btn btn-rounded-primary"
                    v-if="post.post_content && post.post_content.length && step !== 0"
                    @click="onPrevStep"
                  >Schritt zurück</button>
                </div>

                <div class="ml-auto">
                  <button
                    class="btn btn-rounded-danger"
                    v-if="post.type === 'diy' && post.post_content.length > 1"
                    @click="onDeleteStep"
                  >Schritt löschen</button>

                  <button
                    class="btn btn-rounded-primary ml-4"
                    v-if="post.type === 'diy' && step !== post.post_content.length"
                    :disabled="!isStepValid"
                    @click="onNextStep"
                  >Schritt vor</button>

                  <button
                    class="btn btn-rounded-primary ml-4"
                    v-if="post.type === 'diy' && step === post.post_content.length"
                    :disabled="!isStepValid"
                    @click="onNextStep"
                  >Schritt hinzufügen</button>
                </div>
              </div>
            </ci-card>
          </div>
        </div>
      </div>

      <!-- Sidebar -->
      <div class="col-3">
        <ci-card type="dark">
          <label class="mt-3" :for="`${_uid}-slug`">Slug</label>
          <input
            type="text"
            :class="getInputClass('slug.slug')"
            :id="`${_uid}-slug`"
            placeholder="Slug eingeben…"
            :disabled="step > 0"
            v-model="post.slug.slug"
            @blur="setSlug(post.slug.slug)"
          >

          <label class="mt-3" :for="`status-select-${_uid}`">{{ $t('post.label.status') }}</label>
          <select
            :class="getInputClass('status')"
            :id="`status-select-${_uid}`"
            v-model="post.status"
          >
            <option
              v-for="status in availableStatus"
              :key="`status-${status.status}-${_uid}`"
              :value="status.status"
            >{{ status.title }}</option>
          </select>
          <label class="mt-3" :for="`type-select-${_uid}`">{{ $t('post.label.type') }}</label>
          <select :class="getInputClass('type')" :id="`type-select-${_uid}`" v-model="post.type">
            <option
              v-for="type in availableTypes"
              :key="`type-${type.type}-${_uid}`"
              :value="type.type"
            >{{ type.title }}</option>
          </select>

          <label class="mt-3" :for="`language-select-${_uid}`">Sprache</label>
          <select
            :class="getInputClass('language')"
            :id="`language-select-${_uid}`"
            v-model="post.language"
          >
            <option
              v-for="language in availableLanguages"
              :key="`type-${language.lang}-${_uid}`"
              :value="language.lang"
            >{{ language.title }}</option>
          </select>

          <label class="mt-3" :for="`user-select-${_uid}`">Erstellt von</label>
          <select
            :class="getInputClass('created_by')"
            :id="`user-select-${_uid}`"
            v-model="post.created_by"
          >
            <option
              v-for="user in users"
              :key="`user-${user.id}-${_uid}`"
              :value="user.id"
            >{{ user.username }}</option>
          </select>

          <ci-date-picker
            class="mt-3"
            label="Veröffentlichen am…"
            :pre-select-date="post.publish_at"
            @date-picker:date-selected="onPublishDateSelected"
            :disabled="post.status === 'published'"
          />
          <ci-date-picker
            label="Erstellt am…"
            :pre-select-date="post.created_at"
            @date-picker:date-selected="onCreatedAtDateSelected"
          />

          <div>
            <label class="form-check-label" :for="`${_uid}-featured`">Featured</label>
          </div>
          <div>
            <input
              type="checkbox"
              :id="`${_uid}-featured`"
              :disabled="step > 0"
              v-model="post.featured"
            >
          </div>
        </ci-card>

        <ci-card class="mt-3" :type="getCardType(`post_content.${step}.image_id`)">
          <label>{{ $t('post.label.teaser_image') }}</label>
          <ci-dropzone
            v-if="!post.getStep(step).image"
            accept="image/*"
            @dropzone:files-selected="onFilesSelected"
          />
          <ci-image-focal
            v-else
            deletable
            :image="post.getStep(step).image"
            :focal-point="post.getStep(step).image.focal_point"
            @image:delete="onImageDelete"
            @image:set-focalpoint="setFocalPoint"
          />
        </ci-card>

        <ci-card class="mt-3" :type="getCardType('categories')">
          <ci-category-selector
            :display-children="true"
            :pre-selected-categories="post.categories"
            @category-selector:selected="onCategorySelected"
          >Kategorie auswählen</ci-category-selector>
        </ci-card>

        <ci-card
          class="mt-3"
          :type="getCardType('main_category.id')"
          v-if="getAvailableMainCategories.length"
        >
          <ci-category-selector
            :display-children="true"
            :single-select="true"
            :parent-selectable="true"
            :pre-selected-categories="post.main_category ? [post.main_category] : []"
            :available-categories="getAvailableMainCategories"
            @category-selector:selected="onMainCategorySelected"
          >Hauptkategorie auswählen</ci-category-selector>
        </ci-card>

        <ci-card class="mt-3" :type="getCardType('json')">
          <ci-json-editor
            @json-editor:set-json-validation="onJsonValidation"
            @json-editor:set-json="onJsonSet"
            :meta-info="post.meta_info"
          />
        </ci-card>

        <ci-alerts class="mt-3" :messages.sync="messages"/>
        <div class="d-flex justify-content-end">
          <button
            class="btn btn-rounded-primary mt-3 ml-auto"
            @click="onSave"
            :disabled="!editorReady"
          >Submit</button>
        </div>
      </div>
    </div>
    <div class="col-12 col-xs-12 col-md-12 col-xl-12 mt-4" v-if="post">
      <ci-modal v-if="showModal">
        <ci-modal-header>Bild löschen</ci-modal-header>
        <ci-modal-body>Möchten Sie das Bild wirklich löschen?</ci-modal-body>
        <ci-modal-footer>
          <button
            type="button"
            class="btn btn-secondary"
            data-dismiss="modal"
            @click="showModal = false"
          >Schließen</button>
          <button type="button" class="btn btn-danger" @click="deleteImage">Löschen</button>
        </ci-modal-footer>
      </ci-modal>
    </div>
  </div>
</template>

<script>
import AlertMessages from '@/components/alerts/AlertMessages.vue';
import Card from '@/components/Card.vue';
import CategorySelector from '@/components/CategorySelector.vue';
import DatePicker from '@/components/DatePicker.vue';
import Dropzone from '@/components/Dropzone.vue';
import Editor from '@/components/editor/Editor.vue';
import ImageFocal from '@/components/ImageFocal.vue';
import JsonEditor from '@/components/JsonEditor.vue';
import LanguageSelector from '@/components/LanguageSelector.vue';
import Modal from '@/components/Modal.vue';
import ModalBody from '@/components/ModalBody.vue';
import ModalHeader from '@/components/ModalHeader.vue';
import ModalFooter from '@/components/ModalFooter.vue';

// models
import ImageModel from '@/models/ImageModel';
import PostModel from '@/models/PostModel';

// mixins
import fieldValidation from '@/mixins/fieldValidation';
import getSlug from '@/mixins/getSlug';

// services
import PostService from '@/services/PostService';
import FileService from '@/services/FileService';
import UserService from '@/services/UserService';

// moment
import moment from 'moment';

export default {
  name: 'PostCreateEdit',

  mixins: [fieldValidation, getSlug],

  components: {
    'ci-alerts': AlertMessages,
    'ci-card': Card,
    'ci-category-selector': CategorySelector,
    'ci-date-picker': DatePicker,
    'ci-dropzone': Dropzone,
    'ci-editor': Editor,
    'ci-image-focal': ImageFocal,
    'ci-modal': Modal,
    'ci-modal-body': ModalBody,
    'ci-modal-footer': ModalFooter,
    'ci-modal-header': ModalHeader,
    'ci-json-editor': JsonEditor,
    'ci-language-selector': LanguageSelector,
  },

  props: {
    id: {
      type: String,
      required: false,
      default: '',
    },
  },

  data() {
    return {
      availableLanguages: [
        {
          lang: 'de',
          title: 'Deutsch',
        },
        {
          lang: 'en',
          title: 'Englisch',
        },
      ],
      availableStatus: [
        {
          status: 'draft',
          title: this.$t('post.status.draft'),
        },
        {
          status: 'published',
          title: this.$t('post.status.published'),
        },
      ],
      availableTypes: [
        {
          type: 'single',
          title: this.$t('post.types.single'),
        },
        {
          type: 'diy',
          title: this.$t('post.types.diy'),
        },
      ],
      editorReady: true,
      step: 0,
      users: [],
      messages: [],
      post: null,
      showModal: false,
    };
  },

  created() {
    if (this.id) {
      this.getPost();
    } else {
      this.post = new PostModel();
    }

    this.getUsers().then(() => {
      if (!this.id) {
        this.post.set('created_by', this.$store.getters.getUser.id);
      }
    });
  },

  watch: {
    id(newVal, oldVal) {
      if (newVal && newVal !== oldVal) {
        this.getPost();
      }
      if (newVal) {
        this.getPost();
      } else {
        this.post = new PostModel();
      }
    },

    post: {
      handler() {
        this.resetValidation();
        this.messages = [];
      },
      deep: true,
    },
  },

  computed: {
    getEditorClass() {
      if (
        this.validationErrors.indexOf(`post_content.${this.step}.content`) !==
        -1
      ) {
        return 'is-invalid';
      }

      return '';
    },

    isPostValid() {
      const posts = this.post.post_content;
      let isValid = true;
      for (let i = 0; i < posts.length; i += 1) {
        const step = posts[i];
        isValid = this.validateStep(step);
      }

      return isValid;
    },

    isStepValid() {
      const step = this.post.getStep(this.step);
      const isValid = this.validateStep(step);
      return isValid;
    },

    getAvailableMainCategories() {
      return this.post.categories.filter(category => category.category_id !== null);
    },
  },

  methods: {
    deleteImage() {
      this.post.getStep(this.step).image = null;
      this.post.getStep(this.step).image_id = null;
      this.post.getStep(this.step).focal_point = {};
      this.showModal = false;
    },

    getPost() {
      PostService.findOne(this.id).then((post) => {
        this.post = new PostModel(post);
      });
    },

    getUsers() {
      return new Promise((resolve, reject) => {
        UserService.findAll()
          .then((users) => {
            this.users = users.list;
            return resolve();
          })
          .catch(() => reject());
      });
    },

    validateStep(step) {
      const isStepValid = !!(
        step.title &&
        step.subtitle &&
        step.image &&
        step.content &&
        step.content !== '<p></p>' &&
        step.content !== '<p><br></p>'
      );
      return isStepValid;
    },

    onCategorySelected(payload) {
      this.post.set('categories', payload);
      if (this.post.main_category) {
        const filteredCategory = this.post.categories.filter(category => category.id === this.post.main_category.id);
        if (!filteredCategory.length) {
          this.post.set('main_category', null);
        }
      }
    },

    onJsonValidation(payload) {
      if (!payload) {
        this.validationErrors.push('json');
      }

      if (payload) {
        const jsonIndex = this.validationErrors.indexOf('json');
        if (jsonIndex !== -1) {
          this.validationErrors.splice(jsonIndex, 1);
        }
      }
    },

    onJsonSet(payload) {
      this.post.set('meta_info', payload);
    },

    onMainCategorySelected(payload) {
      this.post.set('main_category', payload[0]);
    },

    onCreatedAtDateSelected(payload) {
      this.post.set('created_at', moment(payload.data).format('YYYY-MM-DD'));
    },

    onEditorReadyStateChange(value) {
      this.editorReady = value;
    },

    onFilesSelected(files) {
      this.$emit('files-selected', files);
      FileService.uploadImages(files).then((response) => {
        const image = response[0];
        this.post.getStep(this.step).set('image', new ImageModel(image));
        this.post.getStep(this.step).set('image_id', image.id);
      });
    },

    onImageDelete() {
      this.showModal = true;
    },

    onNextStep() {
      window.scrollTop = 0;
      this.step += 1;
      if (!this.post.getStep(this.step)) {
        this.post.addStep();
      }
    },

    onDeleteStep() {
      this.post.removeStep(this.step);
      if (this.step !== 0) {
        this.step -= 1;
      }
    },

    onPrevStep() {
      window.scrollTop = 0;
      this.step -= 1;
    },

    onPublishDateSelected(payload) {
      this.post.set('publish_at', moment(payload.data).format('YYYY-MM-DD'));
    },

    onSave() {
      this.resetValidation();
      this.post
        .save()
        .then(() => {
          this.messages = [];
          this.messages.push({
            title: 'Super!',
            msg: 'Der Beitrag wurde erfolgreich gespeichert.',
            type: 'success',
          });
        })
        .catch((errors) => {
          this.messages = [];
          if (Array.isArray(errors)) {
            this.setInvalidFields(errors);
            this.messages.push({
              title: 'Fehler',
              msg:
                'Es ist ein Fehler aufgetreten. Mindestens ein Pflichtfeld fehlt und wurde rot markiert.',
              type: 'danger',
            });
          }
        });
    },

    onSetMeta(json) {
      this.post.set('metaInfo', json);
    },

    setFocalPoint(focalPoint) {
      this.post.getStep(this.step).image.set('focal_point', focalPoint);
    },

    setSlug(value) {
      if (
        this.step === 0 &&
        (!this.post.id || this.post.status !== 'published')
      ) {
        this.post.slug.slug = this.getSlug(value);
      }
    },
  },
};
</script>
